/**
 * Radio toggle for interval scheduling mode + staggered time preview.
 */

import { Clock, Globe, Flag } from 'lucide-react';

import { cn } from '@/lib/utils';
import { ModalSection } from '@/features/smswiser-campaigns/components/ScaleDropModal/shared';

import type { IntervalMode } from '../shared';
import type { IntervalPreviewItem } from '../hooks/useMultiCountryIntervals';

interface Props {
  mode: IntervalMode;
  onChange: (mode: IntervalMode) => void;
  preview: IntervalPreviewItem[];
  hasSchedule: boolean;
  hasInterval: boolean;
  disabled?: boolean;
}

const MODE_OPTIONS: Array<{
  value: IntervalMode;
  icon: typeof Globe;
  label: string;
  description: string;
}> = [
  {
    value: 'one_sequence',
    icon: Globe,
    label: 'Treat as one sequence',
    description: 'All drops numbered globally across countries, interleaved.',
  },
  {
    value: 'per_country',
    icon: Flag,
    label: 'Per country',
    description: 'Each country starts at base time, intervals run independently.',
  },
];

export function IntervalModeSection({ mode, onChange, preview, hasSchedule, hasInterval, disabled }: Props) {
  if (!hasSchedule || !hasInterval) return null;

  return (
    <ModalSection title="Interval Mode" icon={<Clock className="h-3.5 w-3.5" />}>
      <div className="space-y-3">
        <div className="grid gap-2 sm:grid-cols-2">
          {MODE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(opt.value)}
              className={cn(
                'flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors',
                mode === opt.value
                  ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                  : 'border-border hover:border-primary/40 hover:bg-muted/30',
                disabled && 'cursor-not-allowed opacity-50',
              )}
            >
              <opt.icon className={cn('mt-0.5 h-4 w-4 shrink-0', mode === opt.value ? 'text-primary' : 'text-muted-foreground')} />
              <div>
                <p className="text-sm font-medium">{opt.label}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">{opt.description}</p>
              </div>
            </button>
          ))}
        </div>

        {preview.length > 0 && (
          <IntervalPreview items={preview.slice(0, 12)} total={preview.length} />
        )}
      </div>
    </ModalSection>
  );
}

function IntervalPreview({ items, total }: { items: IntervalPreviewItem[]; total: number }) {
  return (
    <div className="rounded-md border border-border/60 bg-muted/20 p-2">
      <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Preview ({total} drops)
      </p>
      <div className="space-y-0.5">
        {items.map((item, i) => (
          <div key={item.id} className="flex items-center gap-2 text-xs">
            <span className="w-5 text-right text-muted-foreground">{i + 1}.</span>
            <span className="rounded bg-primary/10 px-1 py-0.5 text-[10px] font-medium text-primary">
              {item.countryKey}
            </span>
            <span className="font-mono text-muted-foreground">
              {formatPreviewTime(item.executeAfter)}
            </span>
          </div>
        ))}
        {total > items.length && (
          <p className="mt-1 text-[10px] text-muted-foreground">
            ...and {total - items.length} more
          </p>
        )}
      </div>
    </div>
  );
}

function formatPreviewTime(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  });
}
